import { useState, useEffect } from 'react'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import { getExploreRowById, getAllTags, getAllExploreRows } from '../graphql/queries'
import { updateExploreRow, deleteExploreRow } from '../graphql/mutation'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Grid,
  Checkbox,
  IconButton
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'

export default function ExploreRowEditModal({ open, onClose, exploreRowId }) {
  const client = useApolloClient()

  const [exploreRow, setExploreRow] = useState({
    order: 1,
    isHighlighted: false,
    contentId: '',
    contentType: 'PROMPT',
    platform: ['IOS', 'ANDROID'],
    tagId: '',
    id: '',
    createdAt: '',
    updatedAt: ''
  })

  const [exploreRowData, setExploreRowData] = useState(null)
  const [progress, setProgress] = useState(false)
  const [deleteProgress, setDeleteProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { loading, error, data, refetch } = useQuery(getExploreRowById, {
    variables: { id: exploreRowId },
    skip: !exploreRowId || !open,
    onCompleted: data => {
      setExploreRowData(data.getExploreRowById)
    },
  })

  const { data: tagsData } = useQuery(getAllTags)

  useEffect(() => {
    if (exploreRowData) {
      setExploreRow({
        order: exploreRowData.order,
        isHighlighted: exploreRowData.isHighlighted,
        contentId: exploreRowData.contentId,
        contentType: exploreRowData.contentType,
        platform: exploreRowData.platform || ['IOS', 'ANDROID'],
        tagId: exploreRowData.tagId || '',
        id: exploreRowData.id,
        createdAt: exploreRowData.createdAt,
        updatedAt: exploreRowData.updatedAt
      })
    }
  }, [exploreRowData])

  useEffect(() => {
    if (open && exploreRowId) {
      refetch()
    }
  }, [open, exploreRowId, refetch])

  const [updateExploreRowMutation] = useMutation(updateExploreRow)
  const [deleteExploreRowMutation] = useMutation(deleteExploreRow)

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  const handleChange = (field, value) => {
    setExploreRow(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleUpdateExploreRow = async () => {
    if (!exploreRow.contentId.trim()) {
      setSnackBarMessage('Content ID is required')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    if (exploreRow.order < 1) {
      setSnackBarMessage('Order must be at least 1')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    setProgress(true)
    try {
      const variables = {
        id: exploreRow.id,
        order: exploreRow.order,
        isHighlighted: exploreRow.isHighlighted,
        contentId: exploreRow.contentId.trim(),
        contentType: exploreRow.contentType,
        platform: exploreRow.platform?.[0], // Send single platform value
        tagId: exploreRow.tagId || null
      }

      const response = await updateExploreRowMutation({
        variables,
        refetchQueries: [{ query: getAllExploreRows }]
      })

      if (response.data) {
        setSnackBarMessage('Explore row updated successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        setTimeout(() => {
          onClose()
        }, 1000)
      }
    } catch (error) {
      setSnackBarMessage(`Error updating explore row: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleDeleteExploreRow = async () => {
    setDeleteProgress(true)
    try {
      const response = await deleteExploreRowMutation({
        variables: {
          id: exploreRow.id
        },
        refetchQueries: [{ query: getAllExploreRows }]
      })

      if (response.data) {
        setSnackBarMessage('Explore row deleted successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        setTimeout(() => {
          onClose()
        }, 1000)
      }
    } catch (error) {
      setSnackBarMessage(`Error deleting explore row: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setDeleteProgress(false)
      setDeleteDialogOpen(false)
    }
  }

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true)
  }

  const getContentTypeColor = (type) => {
    switch (type) {
      case 'PROMPT': return 'primary'
      case 'STYLE': return 'secondary'
      case 'SECTION': return 'success'
      default: return 'default'
    }
  }

  const availableTags = tagsData?.getAllTags || []

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  if (error || !exploreRowData) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error">
            {error ? `Error loading explore row: ${error.message}` : 'Explore row not found'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Edit Explore Row
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Order"
                type="number"
                value={exploreRow.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1 }}
                helperText="Display order in explore section"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exploreRow.isHighlighted}
                      onChange={(e) => handleChange('isHighlighted', e.target.checked)}
                    />
                  }
                  label="Highlighted"
                />
                {exploreRow.isHighlighted && exploreRow.tagId && (
                  <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                    <Typography variant="body2">
                      Only one content item per tag can be highlighted. Setting this as highlighted will automatically remove highlighting from other items in the same tag.
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Grid>

            {/* Content Information */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Content Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={exploreRow.contentType}
                  label="Content Type"
                  onChange={(e) => handleChange('contentType', e.target.value)}
                >
                  <MenuItem value="PROMPT">Prompt</MenuItem>
                  <MenuItem value="STYLE">Style</MenuItem>
                  <MenuItem value="SECTION">Section</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                <Chip 
                  label={exploreRow.contentType} 
                  color={getContentTypeColor(exploreRow.contentType)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content ID"
                value={exploreRow.contentId}
                onChange={(e) => handleChange('contentId', e.target.value)}
                helperText="ID of the referenced content (Prompt, Style, or Section)"
              />
            </Grid>

            {/* Platform Display (Read-only) */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Platform
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This explore row is for a specific platform. Each platform has its own explore row. To add this content to another platform, create a new explore row.
                </Typography>
              </Alert>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {exploreRow.platform?.includes('IOS') && (
                  <Chip
                    icon={<AppleIcon />}
                    label="iOS"
                    size="medium"
                    sx={{
                      backgroundColor: '#000',
                      color: '#fff',
                      '& .MuiChip-icon': { color: '#fff' }
                    }}
                  />
                )}
                {exploreRow.platform?.includes('ANDROID') && (
                  <Chip
                    icon={<AndroidIcon />}
                    label="Android"
                    size="medium"
                    sx={{
                      backgroundColor: '#3DDC84',
                      color: '#000',
                      '& .MuiChip-icon': { color: '#000' }
                    }}
                  />
                )}
                {!exploreRow.platform?.length && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No platform assigned
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Tag Information */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Tag Information (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Tag</InputLabel>
                <Select
                  value={exploreRow.tagId}
                  label="Tag"
                  onChange={(e) => handleChange('tagId', e.target.value)}
                >
                  <MenuItem value="">No Tag</MenuItem>
                  {availableTags.map((tag) => (
                    <MenuItem key={tag.id} value={tag.id}>
                      {tag.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Tags help organize content. If highlighted, only one item per tag can be highlighted at a time.
              </Typography>
            </Grid>

            {/* Metadata */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Metadata
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="ID"
                value={exploreRow.id}
                disabled
                helperText="Unique identifier"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Created At"
                value={new Date(exploreRow.createdAt).toLocaleString()}
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Updated At"
                value={new Date(exploreRow.updatedAt).toLocaleString()}
                disabled
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={progress || deleteProgress}
          >
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={onClose}
            disabled={progress}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={progress ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleUpdateExploreRow}
            disabled={progress}
          >
            {progress ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this explore row? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteProgress}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteExploreRow}
            color="error"
            variant="contained"
            disabled={deleteProgress}
            startIcon={deleteProgress ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteProgress ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={6000}
        onClose={handleSnackBarClose}
      >
        <Alert onClose={handleSnackBarClose} severity={snackBarSeverity}>
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

